import { apiUrl } from '@/lib/env'
import { ApiError } from '@/lib/api/error'
import { getAccessToken } from '@/lib/api/token'

export type ApiRequestInit = Omit<RequestInit, 'body'> & {
  /** Serialized as JSON. Use `rawBody` for anything else. */
  body?: unknown
  rawBody?: BodyInit
  /** Skip the Authorization header — for the handful of public routes. */
  anonymous?: boolean
}

/**
 * A single call to the Lambda API.
 *
 * Native fetch rather than `constellation-sdk`'s axios client, for three
 * reasons: it works unchanged inside an RSC, it keeps axios out of the browser
 * bundle, and it avoids the SDK constructing a second Supabase client with a
 * different session store (see `lib/supabase/client.ts`). The SDK's DTOs are
 * still the source of truth for what goes over the wire — they are imported as
 * types by the query and mutation layers.
 *
 * Every API handler answers `{ success, errorCode }`, including on failure, so
 * the error path reads `errorCode` out of the body rather than inventing a
 * message from the status line.
 */
export async function apiFetch<T>(
  path: string,
  init: ApiRequestInit = {},
): Promise<T> {
  const { body, rawBody, anonymous, headers, ...rest } = init

  const requestHeaders = new Headers(headers)

  if (!anonymous) {
    const token = await getAccessToken()
    if (token) {
      requestHeaders.set('Authorization', `Bearer ${token}`)
    }
  }

  if (body !== undefined && !requestHeaders.has('Content-Type')) {
    requestHeaders.set('Content-Type', 'application/json')
  }

  const response = await fetch(`${apiUrl()}${path}`, {
    ...rest,
    headers: requestHeaders,
    body: body !== undefined ? JSON.stringify(body) : rawBody,
    /* Authenticated data must never land in Next's data cache — it is keyed by
       URL, not by session, so a cached response would be served to whoever
       asked next. Freshness is React Query's job, not the fetch cache's. */
    cache: 'no-store',
  })

  const payload = await readBody(response)

  if (!response.ok) {
    throw new ApiError(errorMessage(response, payload), {
      status: response.status,
      errorCode: errorCodeOf(payload),
      body: payload,
    })
  }

  /* A 200 carrying `success: false` is a failure the handlers express in the
     body rather than the status line — `/graphql` and several writes do this.
     Surfacing it as a resolved promise would let a caller render an error as
     data. */
  if (isFailureEnvelope(payload)) {
    throw new ApiError(errorMessage(response, payload), {
      status: response.status,
      errorCode: errorCodeOf(payload),
      body: payload,
    })
  }

  return payload as T
}

/**
 * A call whose response is a FILE, not JSON.
 *
 * `apiFetch` reads every body — it would turn a PNG into a lossy string. This
 * shares the auth header and error handling but hands back the raw `Response`,
 * so the caller can take a Blob from it.
 *
 * Errors are still read as JSON: the handlers answer failures with the usual
 * envelope even on routes whose success path is binary.
 */
export async function apiDownload(
  path: string,
  init: ApiRequestInit = {},
): Promise<Response> {
  const { body, rawBody, anonymous, headers, ...rest } = init

  const requestHeaders = new Headers(headers)

  if (!anonymous) {
    const token = await getAccessToken()
    if (token) requestHeaders.set('Authorization', `Bearer ${token}`)
  }

  if (body !== undefined && !requestHeaders.has('Content-Type')) {
    requestHeaders.set('Content-Type', 'application/json')
  }

  const response = await fetch(`${apiUrl()}${path}`, {
    ...rest,
    headers: requestHeaders,
    body: body !== undefined ? JSON.stringify(body) : rawBody,
    cache: 'no-store',
  })

  if (!response.ok) {
    const payload = await readBody(response)
    throw new ApiError(errorMessage(response, payload), {
      status: response.status,
      errorCode: errorCodeOf(payload),
      body: payload,
    })
  }

  return response
}

async function readBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? ''

  if (!contentType.includes('application/json')) {
    const text = await response.text()
    return text || null
  }

  try {
    return await response.json()
  } catch {
    /* A truncated or empty JSON body should not mask the status code that
       actually explains the failure. */
    return null
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isFailureEnvelope(payload: unknown): boolean {
  return isRecord(payload) && payload.success === false
}

function errorCodeOf(payload: unknown): string | undefined {
  if (isRecord(payload) && typeof payload.errorCode === 'string') {
    return payload.errorCode
  }
  return undefined
}

function errorMessage(response: Response, payload: unknown): string {
  const code = errorCodeOf(payload)
  if (code) {
    return `${response.status} ${code}`
  }
  if (isRecord(payload) && typeof payload.message === 'string') {
    return `${response.status} ${payload.message}`
  }
  return `${response.status} ${response.statusText || 'Request failed'}`
}
